/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { courtSearchApi } from "@/lib/api";
import HelpButton from "@/components/HelpButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MagnifyingGlass, Scales, Info, Clock, ArrowSquareOut, Eye, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_LABELS = {
    case_no: "Case number",
    cnr: "CNR (eCourts unique ID)",
    party: "Party name",
    advocate: "Advocate name",
    court: "Court / bench",
    keyword: "Keyword in judgement",
};

export default function CourtSearch() {
    const [providers, setProviders] = useState([]);
    const [v, setV] = useState({ query_type: "keyword", query_value: "", court: "any", year: "" });
    const [results, setResults] = useState(null);
    const [providerUsed, setProviderUsed] = useState(null);
    const [providerError, setProviderError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [history, setHistory] = useState([]);
    const [docOpen, setDocOpen] = useState(false);
    const [docContent, setDocContent] = useState(null);
    const [docBusy, setDocBusy] = useState(false);

    useEffect(() => {
        courtSearchApi.providers().then(setProviders).catch(() => {});
        courtSearchApi.history().then(setHistory).catch(() => {});
    }, []);

    const indianKanoonReady = providers.find((p) => p.key === "indian_kanoon")?.available;

    const search = async () => {
        if (!v.query_value.trim()) { toast.error("Enter a search value"); return; }
        setBusy(true);
        try {
            const r = await courtSearchApi.search({
                ...v,
                year: v.year ? parseInt(v.year, 10) : null,
                court: v.court === "any" ? null : v.court,
            });
            setResults(r.results);
            setProviderUsed(r.provider);
            setProviderError(r.provider_error);
            if (r.provider_error) {
                toast.warning(r.provider_error, { duration: 8000 });
            }
            courtSearchApi.history().then(setHistory).catch(() => {});
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Search failed");
        }
        setBusy(false);
    };

    const viewDoc = async (tid) => {
        setDocBusy(true);
        setDocOpen(true);
        setDocContent(null);
        try {
            const d = await courtSearchApi.fetchDoc(tid);
            setDocContent(d);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to fetch document");
            setDocOpen(false);
        }
        setDocBusy(false);
    };

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="court-search-page">
            <div className="rule-line pt-3 mb-6">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Research</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Court records search</h1>
                    <HelpButton topic="court_search" testid="help-court-search" />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-3">
                    {indianKanoonReady ? (
                        <div className="border border-emerald-300 bg-emerald-50/60 rounded-sm p-3 text-sm" data-testid="provider-banner-live">
                            <div className="flex items-start gap-2">
                                <CheckCircle size={14} weight="fill" className="text-emerald-700 mt-0.5 shrink-0" />
                                <div>
                                    <div className="font-medium text-emerald-900">Indian Kanoon — live</div>
                                    <div className="text-xs text-emerald-900/80 mt-1">
                                        Real judgement search across SC, HCs, tribunals, and lower courts. Each search costs ~Rs 0.50 from your Indian Kanoon credit; opening a full judgement costs ~Rs 0.20. Top up at <a href="https://api.indiankanoon.org" className="underline" target="_blank" rel="noreferrer">api.indiankanoon.org</a>.
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="border border-amber-200 bg-amber-50/60 rounded-sm p-3 text-sm" data-testid="provider-banner-stub">
                            <div className="flex items-start gap-2">
                                <Info size={14} weight="fill" className="text-amber-700 mt-0.5 shrink-0" />
                                <div>
                                    <div className="font-medium text-amber-900">Stub provider active</div>
                                    <div className="text-xs text-amber-900/80 mt-1">
                                        Real court-records search requires an Indian Kanoon API key. Add INDIAN_KANOON_API_KEY to backend/.env to enable.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="border border-border bg-white rounded-md p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Labeled label="Search by">
                                <Select value={v.query_type} onValueChange={(t) => setV({ ...v, query_type: t })}>
                                    <SelectTrigger data-testid="search-type"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(TYPE_LABELS).map(([k, lbl]) => <SelectItem key={k} value={k}>{lbl}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Labeled>
                            <Labeled label="Court (optional)">
                                <Select value={v.court} onValueChange={(c) => setV({ ...v, court: c })}>
                                    <SelectTrigger data-testid="search-court"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="any">Any</SelectItem>
                                        <SelectItem value="supreme_court">Supreme Court</SelectItem>
                                        <SelectItem value="delhi_hc">Delhi High Court</SelectItem>
                                        <SelectItem value="bombay_hc">Bombay High Court</SelectItem>
                                        <SelectItem value="calcutta_hc">Calcutta High Court</SelectItem>
                                        <SelectItem value="madras_hc">Madras High Court</SelectItem>
                                        <SelectItem value="karnataka_hc">Karnataka High Court</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Labeled>
                        </div>
                        <div className="mt-3">
                            <Labeled label="Value">
                                <Input
                                    value={v.query_value}
                                    onChange={(e) => setV({ ...v, query_value: e.target.value })}
                                    onKeyDown={(e) => e.key === "Enter" && search()}
                                    placeholder={
                                        v.query_type === "case_no" ? "e.g., WP(C) 1234/2025" :
                                        v.query_type === "cnr" ? "e.g., DLHC0102/2024/000123" :
                                        v.query_type === "party" ? "e.g., Mohd. Arif" :
                                        v.query_type === "advocate" ? "e.g., Mukul Rohatgi" :
                                        v.query_type === "court" ? "e.g., Delhi High Court Bench 2" :
                                        "any text from a judgement / order"
                                    }
                                    data-testid="search-value"
                                />
                            </Labeled>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <Labeled label="Year (optional)">
                                <Input type="number" value={v.year} onChange={(e) => setV({ ...v, year: e.target.value })} placeholder="e.g., 2024" />
                            </Labeled>
                            <div className="flex items-end">
                                <Button onClick={search} disabled={busy || !v.query_value.trim()} className="ml-auto" data-testid="search-submit">
                                    <MagnifyingGlass size={14} /> {busy ? "Searching…" : "Search"}
                                </Button>
                            </div>
                        </div>
                    </div>

                    {providerError && (
                        <div className="border border-destructive/40 bg-destructive/5 rounded-sm p-2 text-xs text-destructive" data-testid="provider-error">
                            {providerError}
                        </div>
                    )}

                    {results && (
                        <div data-testid="search-results">
                            <div className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                                <span>{results.length} result(s)</span>
                                {providerUsed && (
                                    <span className={`chip ${providerUsed === "indian_kanoon" ? "chip-ai" : "chip-doc"}`}>
                                        {providerUsed === "indian_kanoon" ? "Indian Kanoon" : "Stub"}
                                    </span>
                                )}
                            </div>
                            <ul className="space-y-2">
                                {results.map((r) => (
                                    <li key={r.id || r.tid} className="border border-border bg-white rounded-md p-4" data-testid={`result-${r.id || r.tid}`}>
                                        <div className="flex items-start justify-between gap-3 flex-wrap">
                                            <div className="flex-1 min-w-0">
                                                <div className="font-serif text-lg">{r.title}</div>
                                                <div className="text-xs text-muted-foreground mt-0.5">
                                                    {r.court}{r.case_number ? ` · ${r.case_number}` : ""}{r.date ? ` · ${r.date}` : ""}
                                                </div>
                                                {r.snippet && <p className="text-sm mt-2 line-clamp-3">{r.snippet}</p>}
                                            </div>
                                            <div className="flex flex-col items-end gap-1.5 shrink-0">
                                                {r.stage && <span className={`chip ${r.stage === "Disposed" ? "chip-doc" : "chip-ai"}`}>{r.stage}</span>}
                                                {r.tid && (
                                                    <Button size="sm" variant="outline" onClick={() => viewDoc(r.tid)} data-testid={`view-doc-${r.tid}`}>
                                                        <Eye size={12} /> View
                                                    </Button>
                                                )}
                                                {r.url && (
                                                    <a href={r.url} target="_blank" rel="noreferrer" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                                                        IndianKanoon <ArrowSquareOut size={11} />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                <aside className="space-y-3">
                    <div className="border border-border bg-white rounded-md p-3">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Providers</div>
                        <ul className="space-y-2 text-sm">
                            {providers.map((p) => (
                                <li key={p.key} className="flex items-start gap-2">
                                    <Scales size={13} className={p.available ? "text-emerald-700" : "text-muted-foreground"} />
                                    <div>
                                        <div className="font-medium inline-flex items-center gap-1">
                                            {p.label}
                                            {p.available && <span className="chip chip-ai text-[10px]">live</span>}
                                        </div>
                                        <div className="text-xs text-muted-foreground">{p.note}</div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {!!history.length && (
                        <div className="border border-border bg-white rounded-md p-3" data-testid="search-history">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2 inline-flex items-center gap-1">
                                <Clock size={11} /> Recent searches
                            </div>
                            <ul className="space-y-1.5 text-sm">
                                {history.slice(0, 8).map((h) => (
                                    <li key={h.id} className="flex justify-between gap-2 text-xs">
                                        <button
                                            onClick={() => { setV({ query_type: h.query_type, query_value: h.query_value, court: h.court || "any", year: h.year || "" }); }}
                                            className="text-left hover:underline truncate flex-1"
                                        >
                                            <span className="text-muted-foreground">{h.query_type}</span> · {h.query_value}
                                        </button>
                                        <span className="text-muted-foreground shrink-0">{h.result_count}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </aside>
            </div>

            <Dialog open={docOpen} onOpenChange={setDocOpen}>
                <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-serif">{docContent?.title ? stripTags(docContent.title) : "Judgement"}</DialogTitle>
                        <DialogDescription>
                            {docContent?.publishdate}
                            {docContent?.numcites != null && ` · ${docContent.numcites} citations`}
                            {docContent?.numcitedby != null && ` · cited by ${docContent.numcitedby}`}
                        </DialogDescription>
                    </DialogHeader>
                    {docBusy ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">Fetching from Indian Kanoon…</div>
                    ) : docContent ? (
                        <div
                            className="legal-rendered max-w-none text-[15px]"
                            data-testid="doc-html"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: docContent.doc || "" }}
                        />
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function stripTags(s) {
    return (s || "").replace(/<[^>]+>/g, "").trim();
}

function Labeled({ label, children }) {
    return (
        <div className="space-y-1">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
            {children}
        </div>
    );
}
